// app/javascript/controllers/chart_controller.js
import { Controller } from "@hotwired/stimulus";
import ApexCharts from "apexcharts";
import { Theme, Color, BackgroundColor } from "@adobe/leonardo-contrast-colors";
import autoprefixer from "autoprefixer";
import html2canvas from "html2canvas";

/**
 * Simplified Stimulus controller for rendering and updating survey charts (ApexCharts).
 * Uses reactive value observers for real-time updates via Turbo Streams.
 */
export default class extends Controller {
  static values = {
    colors: Array,
    seriesData: Array,
    seriesMeans: Array,
    seriesNames: Array,
    answerLabels: Array,
    datafieldLabels: Array,
    datafieldExclusive: Array,  // Boolean array indicating which data fields have exclusive flag
    originalDatafieldLabels: Array,  // NEW: Original data field labels before axis swap
    qtype: String,
    chartType: String,  // NEW: Chart type from backend (mean_chart, percent_chart, etc.)
    questionText: String,  // Question text for chart title on download
    displayAsPercentage: Boolean,
    hideDataFieldLabels: Boolean,  // NEW: For grid axis swapping
    swapAxes: Boolean,  // NEW: Whether axes are swapped for this chart
    acutextUseBarChart: Boolean,
    gridPercentages: Array,
    surveyId: Number,  // Survey ID for survey-scoped segment filter persistence
    showZeroColumns: Boolean  // Flipper-controlled: show columns with 0% values (hidden by default)
  }

  /**
   * Called when the controller is connected to the DOM.
   * Initializes the chart for the current question type.
   */
  connect() {
    // Qtype 5 is a special case: do not render a chart
    if (this.qtypeValue == "5") {
      return
    }
    this.chart = null;
    this.hiddenSeries = new Set(); // Track which series are hidden
    this.hiddenSegments = new Set();
    this.activeSegmentIndex = null;
    this.legendContainer = null;
    this.segmentLegendButtons = [];
    this.latestAnswerColors = [];
    this.latestAnswerLabels = [];
    this.activeXAxisLabels = []; // Track which labels are actively used on x-axis (for resize)
    this.isStackedPercentGrid = false;
    this.leonardoPaletteCache = new Map();
    this.restoreSegmentFilterState();
    this.initializeChart();

    // Add resize listener to recalculate label wrapping
    this.resizeHandler = this.debounce(() => this.handleResize(), 250);
    window.addEventListener('resize', this.resizeHandler);

    // Attach custom export handlers for question text inclusion
    this.attachExportHandlers();
  }

  /**
   * Called when the controller is disconnected from the DOM.
   * Destroys the chart to prevent memory leaks.
   */
  disconnect() {
    // Remove resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    // Remove word cloud outside-click handler
    if (this.wordCloudClickHandler) {
      document.removeEventListener('click', this.wordCloudClickHandler);
      this.wordCloudClickHandler = null;
    }

    // Remove export click handlers
    this.detachExportHandlers();

    this.removeCustomLegend();
    if (this.chart && typeof this.chart.destroy === 'function') {
      this.chart.destroy();
    }
  }

  /**
   * Returns a sanitized filename for chart exports based on question text.
   * Truncates to 50 chars and removes unsafe characters.
   * @returns {string} Safe filename string
   */
  getExportFilename() {
    const questionText = this.questionTextValue || 'chart';
    // Strip HTML tags, normalize whitespace, remove unsafe filename chars
    const clean = questionText
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .replace(/[<>:"/\\|?*]/g, '')
      .trim()
      .substring(0, 50);
    return clean || 'chart';
  }

  /**
   * Returns toolbar configuration with export buttons.
   * Uses built-in download menu with custom export handlers attached after mount.
   * @returns {Object} ApexCharts toolbar configuration
   */
  getToolbarConfig() {
    const filename = this.getExportFilename();
    return {
      show: true,
      tools: {
        download: true,
        selection: false,
        zoom: false,
        zoomin: false,
        zoomout: false,
        pan: false,
        reset: false
      },
      export: {
        csv: {
          filename: filename,
          headerCategory: 'Category',
          headerValue: 'Value'
        },
        svg: {
          filename: filename
        },
        png: {
          filename: filename
        }
      }
    };
  }

  /**
   * Handles clicks on export menu items using event delegation.
   * Intercepts PNG, SVG, and CSV exports to include question text.
   * @param {Event} e - Click event
   */
  handleExportClick(e) {
    const target = e.target;
    if (!target || !target.classList) return;

    // Check if click is on an export menu item
    if (target.classList.contains('apexcharts-menu-item')) {
      if (target.classList.contains('exportPNG')) {
        e.preventDefault();
        e.stopPropagation();
        this.exportPNG();
        // Close the menu
        const menu = this.element.querySelector('.apexcharts-menu');
        if (menu) menu.classList.remove('apexcharts-menu-open');
      } else if (target.classList.contains('exportSVG')) {
        e.preventDefault();
        e.stopPropagation();
        this.exportSVG();
        const menu = this.element.querySelector('.apexcharts-menu');
        if (menu) menu.classList.remove('apexcharts-menu-open');
      } else if (target.classList.contains('exportCSV')) {
        e.preventDefault();
        e.stopPropagation();
        this.exportCSVWithQuestionText();
        const menu = this.element.querySelector('.apexcharts-menu');
        if (menu) menu.classList.remove('apexcharts-menu-open');
      }
    }
  }

  /**
   * Attaches event delegation for export menu clicks.
   * Uses capture phase to intercept before ApexCharts default handlers.
   */
  attachExportHandlers() {
    if (!this.element) return;
    this.exportClickHandler = (e) => this.handleExportClick(e);
    this.element.addEventListener('click', this.exportClickHandler, true);
  }

  /**
   * Removes export click handler.
   */
  detachExportHandlers() {
    if (this.exportClickHandler && this.element) {
      this.element.removeEventListener('click', this.exportClickHandler, true);
    }
  }

  /**
   * Exports chart as PNG image with question text as title.
   * Temporarily adds title for export, then removes it.
   */
  exportPNG() {
    if (!this.chart) return;
    const filename = this.getExportFilename();
    const questionText = this.questionTextValue || '';

    // Temporarily add title for export, using the 'updated' event to know when rendering is complete
    this.chart.updateOptions({
      title: { text: questionText, align: 'center', style: { fontSize: '14px', fontWeight: 'bold' } },
      chart: {
        events: {
          updated: (_chart, _options) => {
            // Clear the event handler to avoid repeated triggers
            this.chart.updateOptions({ chart: { events: { updated: undefined } } }, false, false);

            this.chart.dataURI().then(({ imgURI }) => {
              const link = document.createElement('a');
              link.href = imgURI;
              link.download = `${filename}.png`;
              link.click();

              // Remove title after export
              this.chart.updateOptions({ title: { text: '' } }, false, false);
            });
          }
        }
      }
    }, false, false);
  }

  /**
   * Exports chart as SVG with question text as title.
   * Temporarily adds title for export, then removes it.
   */
  exportSVG() {
    if (!this.chart) return;
    const filename = this.getExportFilename();
    const questionText = this.questionTextValue || '';

    // Temporarily add title for export, using the 'updated' event to know when rendering is complete
    this.chart.updateOptions({
      title: { text: questionText, align: 'center', style: { fontSize: '14px', fontWeight: 'bold' } },
      chart: {
        events: {
          updated: (_chart, _options) => {
            // Clear the event handler to avoid repeated triggers
            this.chart.updateOptions({ chart: { events: { updated: undefined } } }, false, false);

            // SVG selectors below are coupled to ApexCharts' internal DOM structure
            const chartEl = this.chart.el;
            const svgEl = chartEl ? chartEl.querySelector('svg.apexcharts-svg') : null;

            if (svgEl) {
              const svgClone = svgEl.cloneNode(true);

              // Remove toolbar and menu elements from the export
              const elementsToRemove = svgClone.querySelectorAll(
                '.apexcharts-toolbar, .apexcharts-menu, .apexcharts-reset-icon, ' +
                '.apexcharts-zoom-icon, .apexcharts-pan-icon, .apexcharts-selection-icon'
              );
              elementsToRemove.forEach(el => el.remove());

              // Remove crosshairs and grid border elements that cause unwanted lines
              const unwantedElements = svgClone.querySelectorAll(
                '.apexcharts-ycrosshairs, .apexcharts-xcrosshairs, .apexcharts-grid-borders rect'
              );
              unwantedElements.forEach(el => el.remove());

              svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
              svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
              svgClone.setAttribute('xmlns:data', 'ApexChartsNS');

              svgClone.removeAttribute('transform');
              svgClone.setAttribute('style', 'background: white;');

              const svgData = new XMLSerializer().serializeToString(svgClone);

              const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${filename}.svg`;
              link.click();
              URL.revokeObjectURL(url);
            }

            // Remove title after export
            this.chart.updateOptions({ title: { text: '' } }, false, false);
          }
        }
      }
    }, false, false);
  }

  /**
   * Exports chart data as CSV with question text as the first row.
   * Creates a downloadable CSV file with headers and data.
   */
  exportCSVWithQuestionText() {
    if (!this.chart) return;

    const questionText = this.questionTextValue || 'Chart Data';
    const filename = this.getExportFilename();

    // Get chart data from ApexCharts globals
    const w = this.chart.w;
    const series = w.config.series;
    const categories = w.config.xaxis.categories || [];

    // Build CSV content with question text as first row
    const rows = [];

    // Row 1: Question text
    rows.push([questionText]);

    // Row 2: Empty row for readability
    rows.push([]);

    // Build header row: Category, then series names
    const headerRow = ['Category'];
    series.forEach(s => headerRow.push(s.name || 'Value'));
    rows.push(headerRow);

    // Data rows: category label followed by values for each series
    categories.forEach((category, catIndex) => {
      const row = [Array.isArray(category) ? category.join(' ') : category];
      series.forEach(s => {
        const value = s.data && s.data[catIndex] !== undefined ? s.data[catIndex] : '';
        row.push(value);
      });
      rows.push(row);
    });

    // Convert to CSV string with proper escaping
    const csvContent = rows.map(row =>
      row.map(cell => {
        const str = String(cell);
        // Escape cells containing commas, quotes, or newlines
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',')
    ).join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Returns a unique localStorage key for segment filter persistence.
   * Uses survey ID for survey-scoped persistence across all questions.
   * @returns {string} localStorage key
   */
  getSegmentStorageKey() {
    // Use survey ID for survey-scoped persistence across all questions
    if (this.hasSurveyIdValue && this.surveyIdValue) {
      return `acupoll_segment_filter_survey_${this.surveyIdValue}`;
    }
    // Legacy fallback for charts without survey context
    return `acupoll_segment_filter_${this.element.id || 'default'}`;
  }

  /**
   * Stores the active segment index in localStorage for persistence.
   * @param {number} segmentIndex - The segment index to store
   */
  storeSegmentFilter(segmentIndex) {
    try {
      localStorage.setItem(this.getSegmentStorageKey(), String(segmentIndex));
    } catch (e) {
      // localStorage may be unavailable or full - fail silently
    }
  }

  /**
   * Retrieves the stored segment index from localStorage.
   * @returns {number|null} The stored segment index, or null if not found
   */
  getStoredSegmentFilter() {
    try {
      const stored = localStorage.getItem(this.getSegmentStorageKey());
      if (stored !== null) {
        const index = parseInt(stored, 10);
        return Number.isFinite(index) ? index : null;
      }
    } catch (e) {
      // localStorage may be unavailable - fail silently
    }
    return null;
  }

  /**
   * Restores segment filter state from localStorage during connect.
   * Sets activeSegmentIndex if a valid stored value exists.
   */
  restoreSegmentFilterState() {
    const storedIndex = this.getStoredSegmentFilter();
    if (storedIndex !== null) {
      this.activeSegmentIndex = storedIndex;
    }
    // Also restore hidden series names for non-stacked charts
    const storedHidden = this.getStoredHiddenSeriesNames();
    if (storedHidden && storedHidden.length > 0) {
      this.hiddenSeries = new Set(storedHidden);
    }
  }

  /**
   * Returns a unique localStorage key for hidden series persistence.
   * Uses survey ID for survey-scoped persistence (separate from segment filter).
   * @returns {string} localStorage key
   */
  getHiddenSeriesStorageKey() {
    if (this.hasSurveyIdValue && this.surveyIdValue) {
      return `acupoll_hidden_series_survey_${this.surveyIdValue}`;
    }
    return `acupoll_hidden_series_${this.element.id || 'default'}`;
  }

  /**
   * Stores hidden series names in localStorage for persistence.
   * @param {Set<string>} hiddenSet - Set of hidden series names
   */
  storeHiddenSeriesNames(hiddenSet) {
    try {
      const names = Array.from(hiddenSet || []);
      localStorage.setItem(this.getHiddenSeriesStorageKey(), JSON.stringify(names));
    } catch (e) {
      // localStorage may be unavailable or full - fail silently
    }
  }

  /**
   * Retrieves stored hidden series names from localStorage.
   * @returns {Array<string>|null} Array of hidden series names, or null if not found
   */
  getStoredHiddenSeriesNames() {
    try {
      const stored = localStorage.getItem(this.getHiddenSeriesStorageKey());
      if (stored) {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : null;
      }
    } catch (e) {
      // localStorage may be unavailable or parsing failed - fail silently
    }
    return null;
  }

  /**
   * Handles legend click events for non-stacked charts.
   * Tracks which series are hidden and persists to localStorage.
   * @param {number} seriesIndex - The clicked series index
   */
  handleLegendClick(seriesIndex) {
    if (this.isStackedPercentGrid) return; // Stacked charts use different mechanism

    // ApexCharts toggles visibility after this event, so we need to check current state
    // and toggle our tracking to match what will be the NEW state
    const seriesName = this.seriesNamesValue[seriesIndex];
    if (!seriesName) return;

    // Toggle in our tracking set (ApexCharts will toggle visibility)
    if (this.hiddenSeries.has(seriesName)) {
      this.hiddenSeries.delete(seriesName);
    } else {
      this.hiddenSeries.add(seriesName);
    }

    // Persist to localStorage
    this.storeHiddenSeriesNames(this.hiddenSeries);
  }

  /**
   * Applies stored hidden series state after chart is mounted.
   * Called from the chart's mounted event for non-stacked charts.
   */
  applyStoredHiddenSeries() {
    if (this.isStackedPercentGrid) return; // Stacked charts use different mechanism
    if (!this.chart || !this.hiddenSeries || this.hiddenSeries.size === 0) return;

    // Apply hidden series state
    setTimeout(() => {
      this.hiddenSeries.forEach(seriesName => {
        if (this.seriesNamesValue.includes(seriesName)) {
          this.chart.hideSeries(seriesName);
        }
      });
    }, 10);
  }

  /**
   * Debug helper: Logs container and chart dimensions at render time.
   * Used to diagnose timing issues with CSS application and width calculation.
   */
  logChartDimensions(eventName, chartContext) {
    const chartElement = this.element;
    const parentColumn = chartElement.closest('.col-md-9');
    const svg = chartElement.querySelector('svg.apexcharts-svg');

    const computedStyle = window.getComputedStyle(chartElement);
    const parentStyle = parentColumn ? window.getComputedStyle(parentColumn) : null;

    console.group(`📊 Chart Dimensions - ${eventName}`);
    console.log('Chart Container:', {
      offsetWidth: chartElement.offsetWidth,
      clientWidth: chartElement.clientWidth,
      marginLeft: computedStyle.marginLeft,
      marginRight: computedStyle.marginRight,
      width: computedStyle.width,
      paddingLeft: computedStyle.paddingLeft,
      paddingRight: computedStyle.paddingRight
    });

    if (parentColumn) {
      console.log('Parent Column (.col-md-9):', {
        offsetWidth: parentColumn.offsetWidth,
        clientWidth: parentColumn.clientWidth,
        paddingLeft: parentStyle.paddingLeft,
        paddingRight: parentStyle.paddingRight
      });
    }

    if (svg) {
      console.log('SVG Element:', {
        width: svg.getAttribute('width'),
        offsetWidth: svg.offsetWidth,
        viewBox: svg.getAttribute('viewBox')
      });
    }

    console.log('Expected Behavior:', {
      chartShouldBe: parentColumn ? `${parentColumn.offsetWidth}px (full column width)` : 'N/A',
      currentlyIs: `${chartElement.offsetWidth}px`,
      difference: parentColumn ? `${parentColumn.offsetWidth - chartElement.offsetWidth}px` : 'N/A'
    });

    // Log label wrapping calculations
    const labelCount = this.answerLabelsValue?.length || this.datafieldLabelsValue?.length || 0;
    if (labelCount > 0) {
      const chartWidth = this.getChartWidth();
      const widthPerLabel = Math.floor(chartWidth / labelCount) - 20;
      const fontSize = this.calculateXAxisLabelFontSize(labelCount);

      console.log('Label Wrapping:', {
        totalLabels: labelCount,
        chartWidth: `${chartWidth}px`,
        widthPerLabel: `${widthPerLabel}px`,
        fontSize: fontSize,
        strategy: 'Canvas measureText() with word-boundary wrapping'
      });
    }

    console.groupEnd();
  }

  /**
   * Handle window resize events - recalculate label wrapping for new width.
   * Debounced to avoid excessive recalculations during resize.
   */
  handleResize() {
    if (!this.chart) return;

    // Get current labels - prefer the actively tracked labels from last buildChart() call
    // This ensures we use the correct labels regardless of chart type (stacked vs grouped)
    let currentLabels = [];
    if (this.activeXAxisLabels?.length > 0) {
      // Use actively tracked labels (set in buildChart or initStackedPercentGridChart)
      currentLabels = this.activeXAxisLabels;
    } else if (this.latestAnswerLabels?.length > 0) {
      // Fallback for stacked charts
      currentLabels = this.latestAnswerLabels;
    } else if (this.answerLabelsValue?.length > 0) {
      // Default fallback to answer labels
      currentLabels = this.answerLabelsValue;
    } else if (this.datafieldLabelsValue?.length > 0) {
      // Last resort fallback to data field labels
      currentLabels = this.datafieldLabelsValue;
    }

    if (currentLabels.length === 0) {
      return;
    }

    // Recalculate wrapped labels based on new width
    const wrappedLabels = this.prepareLabels(currentLabels);

    // Recalculate font size based on label count
    const fontSize = this.calculateXAxisLabelFontSize(currentLabels.length);

    // Update chart with new labels AND font size
    if (this.chart.w && this.chart.w.config) {
      this.chart.updateOptions({
        xaxis: {
          categories: wrappedLabels,
          labels: {
            hideOverlappingLabels: false,
            rotate: 0,
            style: {
              fontSize: fontSize,
              fontWeight: 'bold'
            },
            maxHeight: 400
          }
        }
      }, false, true); // redrawPaths=false, animate=true
    }
  }

  /**
   * Debounce utility - delays function execution until after wait period of inactivity
   * @param {Function} func - The function to debounce
   * @param {number} wait - Milliseconds to wait
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Reactive observer: called when seriesData changes via Turbo Stream updates.
   * This is the key method for real-time chart updates.
   */
  seriesDataValueChanged() {
    if (!this.chart) return;
    if (this.isStackedPercentGrid) {
      return;
    }
    if (!this.seriesDataValue) return;
    this.storeSeriesVisibility(); // Store current visibility state
    this.updateChartData(this.seriesDataValue, this.answerLabelsValue);
    this.restoreSeriesVisibility(); // Restore visibility state
  }

  /**
   * Reactive observer: called when seriesMeans changes via Turbo Stream updates.
   */
  seriesMeansValueChanged() {
    if (!this.chart) return;
    if (this.isStackedPercentGrid) {
      return;
    }
    if (!this.seriesMeansValue) return;
    if (this.qtypeValue === "2" && !this.displayAsPercentageValue) {
      // Grid question in mean display mode
      this.storeSeriesVisibility(); // Store current visibility state
      this.updateChartData(this.seriesMeansValue, this.datafieldLabelsValue);
      this.restoreSeriesVisibility(); // Restore visibility state
    } else if (this.qtypeValue === "6" && this.acutextUseBarChartValue) {
      // Acutext bar chart update
      this.buildAcutextBarChart();
    }
  }

  /**
   * Reactive observer: called when answerLabels changes via Turbo Stream updates.
   */
  answerLabelsValueChanged() {
    if (!this.chart) return;
    if (this.isStackedPercentGrid) {
      this.storeSeriesVisibility();
      this.updateStackedPercentGridData();
      this.restoreSeriesVisibility();
      return;
    }
    if (!this.seriesDataValue) return;
    this.storeSeriesVisibility(); // Store current visibility state
    this.updateChartData(this.seriesDataValue, this.answerLabelsValue);
    this.restoreSeriesVisibility(); // Restore visibility state
  }

  gridPercentagesValueChanged() {
    if (!this.chart || !this.isStackedPercentGrid) return;
    this.storeSeriesVisibility();
    this.updateStackedPercentGridData();
    this.restoreSeriesVisibility();
  }

  datafieldLabelsValueChanged() {
    if (!this.chart || !this.isStackedPercentGrid) return;
    this.storeSeriesVisibility();
    this.updateStackedPercentGridData();
    this.restoreSeriesVisibility();
  }

  seriesNamesValueChanged() {
    if (!this.chart || !this.isStackedPercentGrid) return;
    this.renderSegmentLegend();
    this.ensureActiveSegmentIndex();
  }

  colorsValueChanged() {
    if (!this.chart || !this.isStackedPercentGrid) return;
    this.storeSeriesVisibility();
    this.updateStackedPercentGridData();
    this.restoreSeriesVisibility();
  }

  /**
   * Initializes the chart based on question type and data.
   * Handles percent, mean, and Acutext (word cloud) charts.
   */
  initializeChart() {
    const qtype = this.qtypeValue;
    if (this.shouldUseStackedPercentGridChart()) {
      this.isStackedPercentGrid = true;
      this.initStackedPercentGridChart();
      return;
    }
    if (["1", "8"].includes(qtype)) {
      // Single-select & single-to-open-end always percent chart
      this.initPercentChart();
      this.buildChart(this.seriesDataValue, this.answerLabelsValue);
    } else if (qtype === "2") {
      if (this.shouldUsePercentageFormat()) {  // CHANGED: Use new helper
        // Grid configured for percentage display: use percentages (answer options)
        this.initPercentChart();
        this.buildChart(this.seriesDataValue, this.answerLabelsValue);
      } else {
        // Grid mean display: use means (data fields)
        this.initMeanChart();
        this.buildChart(this.seriesMeansValue, this.datafieldLabelsValue);
      }
    } else if (["3"].includes(qtype)) {
      // Multi-select: chart the percent-of-respondents (means array holds percent selected per option)
      this.initPercentChart();
      this.buildChart(this.seriesMeansValue, this.datafieldLabelsValue);
    } else if (qtype == "6") {
      // Acutext: render word cloud or horizontal bar chart
      if (this.acutextUseBarChartValue) {
        this.initAcutextBarChart();
        this.buildAcutextBarChart();
      } else {
        this.chart = this.element;
        this.prepare_acutext();
        this.adjust_acutext();
        this.addWordCloudDownloadButton();
      }
    }
  }

  /**
   * Determines if percentage format should be used for this grid question.
   * Returns true when either display_as_percentage OR hide_data_field_labels is enabled.
   * These two flags serve the same functional purpose.
   * @returns {boolean}
   */
  shouldUsePercentageFormat() {
    return this.displayAsPercentageValue || this.hideDataFieldLabelsValue;
  }

  shouldUseStackedPercentGridChart() {
    return this.qtypeValue === "2" &&
      this.shouldUsePercentageFormat() &&  // CHANGED: Use new helper
      Array.isArray(this.datafieldLabelsValue) &&
      this.datafieldLabelsValue.length > 1 &&
      Array.isArray(this.gridPercentagesValue) &&
      this.gridPercentagesValue.length > 0;
  }

  /**
   * Calculates appropriate font size for data labels based on number of categories.
   * Prevents overlap when there are many bars by reducing font size.
   * @param {number} numCategories - Number of categories/bars in the chart
   * @returns {string} Font size in pixels
   */
  calculateDataLabelFontSize(numCategories) {
    return '12px'; // WCAG 2.1 minimum accessible font size
  }

  /**
   * Calculates appropriate font size for x-axis labels based on number of categories.
   * Ensures all labels fit horizontally without being hidden by ApexCharts.
   * @param {number} numCategories - Number of categories on x-axis
   * @returns {string} Font size in pixels
   */
  calculateXAxisLabelFontSize(numCategories) {
    return '12px'; // WCAG 2.1 minimum accessible font size
  }

  /**
   * Initializes an ApexCharts column chart for mean values.
   */
  initMeanChart() {
    const options = {
      chart: {
        type: 'bar',
        // width omitted - ApexCharts auto-inherits parent container width
        height: 400, // Fixed height to avoid scrolling
        background: 'transparent',
        animations: {
          enabled: false,
          // easing: 'easeinout',
          // speed: 800
        },
        toolbar: this.getToolbarConfig(),
        events: {
          mounted: (chartContext, config) => {
            this.logChartDimensions('MeanChart mounted', chartContext);
            // Apply stored hidden series after chart is mounted
            this.applyStoredHiddenSeries();
          },
          updated: (chartContext, config) => {
            this.logChartDimensions('MeanChart updated', chartContext);
          },
          legendClick: (chartContext, seriesIndex, config) => {
            // Track hidden/shown series and persist to localStorage
            this.handleLegendClick(seriesIndex);
          },
          animationEnd: () => { }
        }
      },
      title: {
        text: '', // Title hidden on display, added dynamically for exports
        align: 'center',
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      series: [], // Will be populated by buildChart
      grid: {
        show: true,
        borderColor: '#90A4AE',
        strokeDashArray: 0,
        position: 'back',
        xaxis: {
          lines: {
            show: false // No horizontal grid lines
          }
        },
        yaxis: {
          lines: {
            show: false // No vertical grid lines
          }
        },
        padding: {
          top: 0,
          right: 10, // Prevent rightmost label clipping
          bottom: 0,
          left: 0
        }
      },
      xaxis: {
        categories: [],
        labels: {
          rotate: 0, // No rotation - keep labels vertical/horizontal
          style: {
            fontSize: this.calculateXAxisLabelFontSize(this.datafieldLabelsValue?.length || this.answerLabelsValue?.length || 0),
            fontWeight: 'bold'
          },
          maxHeight: 400, // Increased from 200 to 400 to accommodate more wrapped lines
          // No formatter: allow arrays for multiline (vertically stacked)
        },
        tickPlacement: 'between',
        //tickAmount: 1,
        axisBorder: {
          show: true,
          color: '#90A4AE',
          height: 1,
          width: '100%',
          offsetX: 0,
          offsetY: 0
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#78909C',
          height: 6,
          offsetX: 0,
          offsetY: 0
        }
      },
      yaxis: {
        min: 0,
        max: 10,
        title: {
          text: "Average",
          offsetX: 4,
          style: {
            fontSize: '10px',
            fontWeight: 'bold'
          }
        },
        labels: {
          show: false
        },
        tickPlacement: 'between',
        tickAmount: 1,
        axisBorder: {
          show: true,
          color: '#90A4AE',
          offsetX: -13,
          offsetY: 0
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#78909C',
          width: 16,
          offsetX: 13,
          offsetY: 0
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '12px',
        fontWeight: '600',
        offsetY: 5,
        markers: {
          shape: 'circle',
          width: 10,
          height: 10,
          offsetX: -2,
          strokeWidth: 0
        },
        itemMargin: {
          horizontal: 4,
          vertical: 4
        },
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      tooltip: {
        enabled: false,
        shared: true,
        intersect: false,
        theme: 'dark', // Use dark theme for white text on gray background
        style: {
          fontSize: '14px',
          fontFamily: 'Verdana, Arial, sans-serif'
        },
        y: {
          formatter: function (value) {
            return value.toFixed(1);
          }
        },
        x: {
          formatter: function (value, { series, seriesIndex, dataPointIndex, w }) {
            // Since we now show full labels, just return the value
            return value;
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: false, // Vertical columns instead of horizontal bars
          dataLabels: {
            position: 'top'
          }
        }
      },
      states: {
        hover: {
          filter: {
            type: 'none'
          }
        },
        active: {
          filter: {
            type: 'none'
          }
        }
      },
      dataLabels: {
        enabled: true,
        offsetY: -25,
        style: {
          fontSize: this.calculateDataLabelFontSize(this.datafieldLabelsValue?.length || this.answerLabelsValue?.length || 0),
          fontWeight: 'bold',
          colors: ['#000000'] // Force black text for readability
        },
        background: {
          enabled: false // No background for mean values on bars
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: '#ffffff',
          opacity: 0.8
        },
        formatter: ((showZeros) => (value) => {
          // Handle null values (used to hide zero bars entirely)
          if (value === null || value === undefined) return '';
          // Hide 0 labels unless Flipper flag enables showing them
          if (!showZeros && value === 0) return '';
          return value.toFixed(1);
        })(this.showZeroColumnsValue)
      },
      colors: this.colorsValue || ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0']
    };

    this.chart = new ApexCharts(this.element, options);
    this.chart.render();
  }

  /**
   * Initializes an ApexCharts column chart for percent values.
   */
  initPercentChart() {
    const options = {
      chart: {
        type: 'bar',
        // width omitted - ApexCharts auto-inherits parent container width
        height: 400, // Fixed height to avoid scrolling
        background: 'transparent',
        animations: {
          enabled: false,
          // easing: 'easeinout',
          // speed: 800
        },
        highlightOnHover: false,
        toolbar: this.getToolbarConfig(),
        offsetX: 0,
        offsetY: 0,
        parentHeightOffset: 0,
        events: {
          mounted: (chartContext, config) => {
            this.logChartDimensions('PercentChart mounted', chartContext);
            // Apply stored hidden series after chart is mounted
            this.applyStoredHiddenSeries();
          },
          updated: (chartContext, config) => {
            this.logChartDimensions('PercentChart updated', chartContext);
          },
          legendClick: (chartContext, seriesIndex, config) => {
            // Track hidden/shown series and persist to localStorage
            this.handleLegendClick(seriesIndex);
          }
        }
      },
      title: {
        text: '', // Title hidden on display, added dynamically for exports
        align: 'center',
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      series: [], // Will be populated by buildChart
      grid: {
        show: true,
        borderColor: '#90A4AE',
        strokeDashArray: 0,
        position: 'back',
        xaxis: {
          lines: {
            show: false // No horizontal grid lines
          }
        },
        yaxis: {
          lines: {
            show: false // No vertical grid lines
          }
        },
        padding: {
          top: 0,
          right: 10, // Prevent rightmost label clipping
          bottom: 0,
          left: 0
        }
      },
      xaxis: {
        categories: [],
        labels: {
          hideOverlappingLabels: false, // Always show all labels with multiline
          rotate: 0, // No rotation - keep labels vertical/horizontal
          style: {
            fontSize: this.calculateXAxisLabelFontSize(this.answerLabelsValue?.length || this.datafieldLabelsValue?.length || 0),
            // fontSize: 10,
            fontWeight: 'bold'
          },
          maxHeight: 400, // Increased from 200 to 400 to accommodate more wrapped lines
          // No formatter: allow arrays for multiline (vertically stacked)
        },
        tickPlacement: 'between',
        //tickAmount: 1,
        axisBorder: {
          show: true,
          color: '#90A4AE',
          height: 1,
          width: '100%',
          offsetX: 0,
          offsetY: 0
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#78909C',
          height: 6,
          offsetX: 0,
          offsetY: 0
        }
      },
      yaxis: {
        min: 0,
        max: 100, // Fixed: percentage charts should scale 0-100, not 0-20
        title: {
          text: "% Selecting",
          offsetX: 4,
          style: {
            fontSize: '10px',
            fontWeight: 'bold'
          }
        },
        labels: {
          show: false
        },
        tickPlacement: 'between',
        tickAmount: 1,
        axisBorder: {
          show: true,
          color: '#90A4AE',
          offsetX: -13,
          offsetY: 0
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#78909C',
          width: 10,
          offsetX: 13,
          offsetY: 0
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '12px',
        fontWeight: '600',
        offsetY: 5,
        markers: {
          shape: 'circle',
          width: 10,
          height: 10,
          strokeWidth: 0,
          offsetX: -2,
        },
        itemMargin: {
          horizontal: 4,
          vertical: 4
        },
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      tooltip: {
        enabled: false,
        shared: true,
        intersect: false,
        theme: 'dark', // Use dark theme for white text on gray background
        style: {
          fontSize: '14px',
          fontFamily: 'Verdana, Arial, sans-serif'
        },
        y: {
          formatter: function (value) {
            if (value === null || value === undefined) return '';
            return value.toFixed(0) + '%';
          }
        },
        x: {
          formatter: function (value, { series, seriesIndex, dataPointIndex, w }) {
            // Since we now show full labels, just return the value
            return value;
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: false, // Vertical columns instead of horizontal bars
          dataLabels: {
            position: 'top'
          }
        }
      },
      states: {
        hover: {
          filter: {
            type: 'none'
          }
        },
        active: {
          filter: {
            type: 'none'
          }
        }
      },
      dataLabels: {
        enabled: true,
        offsetY: -25,
        style: {
          fontSize: this.calculateDataLabelFontSize(this.answerLabelsValue?.length || this.datafieldLabelsValue?.length || 0),
          fontWeight: 'bold',
          colors: ['#000000'] // Force black text for readability
        },
        background: {
          enabled: false // No background for percentage labels on bars
        },
        dropShadow: {
          enabled: true,
          top: 1,
          left: 1,
          blur: 1,
          color: '#ffffff',
          opacity: 0.8
        },
        formatter: ((showZeros) => (value) => {
          // Handle null values (used to hide zero bars entirely)
          if (value === null || value === undefined) return '';
          // Hide 0% labels unless Flipper flag enables showing them
          if (!showZeros && value === 0) return '';
          return value.toFixed(0) + '%';
        })(this.showZeroColumnsValue)
      },
      colors: this.colorsValue || ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0']
    };

    this.chart = new ApexCharts(this.element, options);
    this.chart.render();
  }

  initStackedPercentGridChart() {
    const segments = Array.isArray(this.seriesNamesValue) ? this.seriesNamesValue : [];
    let categories = this.prepareLabels(this.datafieldLabelsValue || []);
    const defaultIndex = this.pickDefaultSegmentIndex(segments);

    // Determine which segment to render - prefer restored value from localStorage, validate bounds
    let activeIndex = defaultIndex;
    if (this.activeSegmentIndex !== null && this.activeSegmentIndex >= 0 && this.activeSegmentIndex < segments.length) {
      activeIndex = this.activeSegmentIndex;
    }

    let segmentSeries = this.buildStackedPercentSeriesForSegment(activeIndex) || { series: [], colors: [] };

    // Track the active x-axis labels for resize handling
    // Note: X-axis labels (categories) are always shown - only 0% data labels are hidden
    this.activeXAxisLabels = this.datafieldLabelsValue || [];

    this.activeSegmentIndex = (segments.length > 0 && Number.isFinite(activeIndex)) ? activeIndex : null;
    this.hiddenSegments = new Set(segments.map((_, idx) => idx).filter((idx) => idx !== this.activeSegmentIndex));

    const options = {
      chart: {
        type: 'bar',
        // width omitted - ApexCharts auto-inherits parent container width
        height: this.calculateStackedChartHeight(),
        background: 'transparent',
        stacked: true,
        stackType: 'normal',  // Changed from '100%' - we're passing percentages, not counts
        animations: {
          enabled: false
        },
        toolbar: this.getToolbarConfig(),
        events: {
          mounted: (chartContext, config) => {
            this.logChartDimensions('StackedPercentChart mounted', chartContext);
          },
          updated: (chartContext, config) => {
            this.logChartDimensions('StackedPercentChart updated', chartContext);
          }
        }
      },
      title: {
        text: '', // Title hidden on display, added dynamically for exports
        align: 'center',
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      series: segmentSeries.series,
      colors: segmentSeries.colors,
      grid: {
        show: true,
        borderColor: '#90A4AE',
        strokeDashArray: 0,
        position: 'back',
        xaxis: {
          lines: { show: false }
        },
        yaxis: {
          lines: { show: false }
        },
        padding: {
          top: 0,
          right: 10, // Prevent rightmost label clipping
          bottom: 0,
          left: 0
        }
      },
      xaxis: {
        categories,
        labels: {
          rotate: 0,
          style: {
            fontSize: this.calculateXAxisLabelFontSize(categories.length),
            fontWeight: 'bold'
          },
          maxHeight: 200
        },
        tickPlacement: 'between',
        axisBorder: {
          show: true,
          color: '#90A4AE',
          height: 1,
          offsetX: 0,
          offsetY: 0
        },
        axisTicks: {
          show: true,
          borderType: 'solid',
          color: '#78909C',
          height: 6,
          offsetX: 0,
          offsetY: 0
        }
      },
      yaxis: {
        min: 0,
        // Remove max to allow auto-scaling based on data
        // This makes better use of vertical space when stacked values don't reach 100%
        labels: {
          show: false,
          // formatter: (value) => `${Math.round(value)}%`,
          // style: {
          //   fontSize: '10px'
          // }
        },
        title: {
          text: '% Selecting',
          offsetX: 4,
          style: {
            fontSize: '10px',
            fontWeight: 'bold'
          }
        },
        axisBorder: {
          show: true,
          color: '#90A4AE',
          height: 1,
          offsetX: 0,
          offsetY: 0
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          dataLabels: {
            position: 'center'
          }
        }
      },
      dataLabels: {
        enabled: true,
        position: 'center',
        offsetY: 2,
        formatter: ((showZeros) => (value) => {
          if (value === null || value === undefined) return '';
          // Hide 0% labels unless Flipper flag enables showing them
          if (!showZeros && value === 0) return '';
          if (value < 1) return '';
          return `${Math.round(value)}%`;
        })(this.showZeroColumnsValue),
        style: {
          fontSize: '10px',
          fontWeight: 'bold',
          colors: ['#000000'],
        },
        background: {
          enabled: true,
          borderWidth: 0,
          borderRadius: 2,
          opacity: 0.375,
          padding: 1,
          foreColor: '#000000',
          backgroundColor: '#ffffff'
        },
        dropShadow: {
          enabled: false,
          top: .25,
          left: .25,
          blur: 3,
          color: '#ffffff',
          opacity: 1
        }
      },
      stroke: {
        width: 1
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '10px',
        fontWeight: '600',
        offsetY: 5,
        markers: {
          shape: 'circle',
          width: 6,
          height: 6,
          strokeWidth: 0
        },
        itemMargin: {
          horizontal: 4,
          vertical: 4
        },
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      tooltip: {
        shared: false,
        intersect: true,
        custom: ({ series: tooltipSeries, seriesIndex, dataPointIndex, w }) => this.renderStackedTooltip(tooltipSeries, seriesIndex, dataPointIndex, w)
      },
      states: {
        hover: {
          filter: { type: 'none' }
        },
        active: {
          filter: { type: 'none' }
        }
      }
    };

    this.chart = new ApexCharts(this.element, options);
    this.chart.render().then(() => {
      this.renderSegmentLegend();
      this.ensureActiveSegmentIndex();
    });
  }

  calculateStackedChartHeight() {
    const fieldCount = Array.isArray(this.datafieldLabelsValue) ? this.datafieldLabelsValue.length : 0;
    if (fieldCount <= 6) {
      return 420;
    }
    return Math.min(900, 420 + (fieldCount - 6) * 40);
  }

  buildStackedPercentSeriesForSegment(segmentIndex) {
    const segments = Array.isArray(this.seriesNamesValue) ? this.seriesNamesValue : [];
    const gridData = Array.isArray(this.gridPercentagesValue) ? this.gridPercentagesValue : [];
    const dataFieldLabels = Array.isArray(this.datafieldLabelsValue) ? this.datafieldLabelsValue : [];

    if (segmentIndex < 0 || segmentIndex >= segments.length) return null;

    const segmentMatrix = gridData[segmentIndex] || [];

    const firstRow = segmentMatrix.find((row) => Array.isArray(row) && row.length > 0);
    const inferredAnswerCount = firstRow ? firstRow.length : 0;
    const answerLabels = Array.isArray(this.answerLabelsValue) && this.answerLabelsValue.length > 0
      ? this.answerLabelsValue
      : Array.from({ length: inferredAnswerCount }, (_, index) => `Option ${index + 1}`);
    const answerCount = answerLabels.length;

    const series = [];
    const colors = [];

    // When axes are swapped (HDFL enabled):
    // - segmentMatrix has been transposed: rows are answer options, columns are data fields
    // - dataFieldLabels contains answer option labels (x-axis categories)
    // - originalDatafieldLabels contains the actual data field labels (for series)
    // - We want to create series for each data field (not answer option)
    // - Each series should have data points for each answer option
    if (this.swapAxesValue) {
      // After transpose: matrix[answerIndex][dataFieldIndex]
      // We want series per data field, data points per answer option
      const numAnswerOptions = segmentMatrix.length;  // Rows in transposed matrix
      const dataFieldCount = inferredAnswerCount;  // Columns in transposed matrix
      
      // Get original data field labels for series names
      const originalDataFieldLabels = Array.isArray(this.originalDatafieldLabelsValue) && this.originalDatafieldLabelsValue.length > 0
        ? this.originalDatafieldLabelsValue
        : Array.from({ length: dataFieldCount }, (_, index) => `Item ${index + 1}`);

      for (let dataFieldIndex = 0; dataFieldIndex < dataFieldCount; dataFieldIndex += 1) {
        // Map over answer options (rows in transposed matrix) to get data for this data field (column)
        const dataPoints = [];
        for (let answerIndex = 0; answerIndex < numAnswerOptions; answerIndex += 1) {
          const answerRow = segmentMatrix[answerIndex] || [];
          const rawValue = answerRow[dataFieldIndex];
          const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
          dataPoints.push(Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(2)) : 0);
        }

        // Use the original data field label for the series name
        const seriesLabel = originalDataFieldLabels[dataFieldIndex] || `Item ${dataFieldIndex + 1}`;
        const color = this.calculateStackColor(segmentIndex, dataFieldIndex, dataFieldCount);

        series.push({
          name: seriesLabel,
          data: dataPoints,
          color,
          meta: {
            segmentIndex,
            segmentName: segments[segmentIndex],
            dataFieldIndex,
            seriesLabel
          }
        });
        colors.push(color);
      }
    } else {
      // Normal (non-swapped) logic: create series per answer option
      for (let answerIndex = 0; answerIndex < answerCount; answerIndex += 1) {
        const dataPoints = dataFieldLabels.map((_, fieldIndex) => {
          const fieldRow = segmentMatrix[fieldIndex] || [];
          const rawValue = fieldRow[answerIndex];
          const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue);
          return Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(2)) : 0;
        });

        const answerLabel = answerLabels[answerIndex] || `Option ${answerIndex + 1}`;
        const color = this.calculateStackColor(segmentIndex, answerIndex, answerCount);

        series.push({
          name: answerLabel,
          data: dataPoints,
          color,
          meta: {
            segmentIndex,
            segmentName: segments[segmentIndex],
            answerIndex,
            answerLabel
          }
        });
        colors.push(color);
      }
    }

    return { series, colors, answerLabels };
  }

  baseColorForSegment(segmentIndex) {
    const palette = Array.isArray(this.colorsValue) && this.colorsValue.length > 0
      ? this.colorsValue
      : ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'];
    if (palette.length === 0) return '#008FFB';
    return palette[segmentIndex % palette.length];
  }

  calculateStackColor(segmentIndex, answerIndex, totalAnswers = 0) {
    // Use full color palette for distinct answer option colors
    // Each answer option (Poor, Fair, Good, etc.) gets a unique color from the palette
    const palette = Array.isArray(this.colorsValue) && this.colorsValue.length > 0
      ? this.colorsValue
      : ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'];
    return palette[answerIndex % palette.length];
  }

  legacyCalculateStackColor(segmentIndex, answerIndex, totalAnswers = 0) {
    const base = this.baseColorForSegment(segmentIndex);
    if (!base) return undefined;
    if (!Number.isFinite(totalAnswers) || totalAnswers <= 1) {
      return base;
    }
    const { min: minFactor, max: maxFactor } = this.contrastFactorRange(totalAnswers);
    const normalized = Math.min(1, Math.max(0, answerIndex / Math.max(1, totalAnswers - 1)));
    const factor = minFactor + normalized * (maxFactor - minFactor);
    return this.adjustColorLegacy(base, factor);
  }

  adjustColorLegacy(hexColor, factor) {
    if (!hexColor || typeof hexColor !== 'string') return hexColor;
    let hex = hexColor.replace('#', '');
    if (hex.length === 3) {
      hex = hex.split('').map((char) => char + char).join('');
    }
    const baseNum = parseInt(hex, 16);
    if (Number.isNaN(baseNum)) return hexColor;

    const clamp = (value) => Math.min(255, Math.max(0, value));

    let r = (baseNum >> 16) & 0xff;
    let g = (baseNum >> 8) & 0xff;
    let b = baseNum & 0xff;

    if (factor > 0) {
      const clamped = Math.min(factor, 1);
      r = clamp(r + (255 - r) * clamped);
      g = clamp(g + (255 - g) * clamped);
      b = clamp(b + (255 - b) * clamped);
    } else if (factor < 0) {
      const clamped = Math.max(0, 1 + factor);
      r = clamp(r * clamped);
      g = clamp(g * clamped);
      b = clamp(b * clamped);
    }

    const adjusted = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    return `#${adjusted}`;
  }

  fetchContrastRamp(segmentIndex, totalAnswers = 0) {
    if (!Number.isFinite(totalAnswers) || totalAnswers <= 0) return null;
    const baseColor = this.baseColorForSegment(segmentIndex);
    if (!baseColor) return null;
    const normalizedColor = this.normalizeHexColor(baseColor);
    if (!normalizedColor) return null;

    const cacheKey = `${normalizedColor}:${totalAnswers}`;
    if (this.leonardoPaletteCache?.has(cacheKey)) {
      return this.leonardoPaletteCache.get(cacheKey);
    }

    try {
      const ramp = this.generateContrastColorRamp(normalizedColor, totalAnswers);
      if (Array.isArray(ramp) && ramp.length === totalAnswers) {
        this.leonardoPaletteCache.set(cacheKey, ramp);
        return ramp;
      }
    } catch (error) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('Leonardo color ramp generation failed', error);
      }
    }

    return null;
  }

  generateContrastColorRamp(baseColor, count) {
    if (!baseColor || count <= 0) return [];

    const ratios = this.generateContrastRatios(count);
    try {
      const background = new BackgroundColor({
        name: 'chartBackground',
        colorKeys: ['#ffffff'],
        ratios
      });

      const segmentColor = new Color({
        name: 'segment',
        colorKeys: [baseColor],
        ratios,
        colorspace: 'CAM02',
        smooth: true,
        output: 'HEX'
      });

      const theme = new Theme({
        colors: [segmentColor],
        backgroundColor: background,
        lightness: this.selectLeonardoLightness(count),
        contrast: this.selectLeonardoContrast(count),
        saturation: 100,
        output: 'HEX'
      });

      const paletteEntry = Array.isArray(theme.contrastColors)
        ? theme.contrastColors.find((entry) => entry?.name === 'segment')
        : null;

      if (!paletteEntry || !Array.isArray(paletteEntry.values)) {
        return this.generateFallbackRamp(baseColor, count);
      }

      const valuesData = paletteEntry.values
        .filter((item) => item && typeof item.value === 'string' && item.value.startsWith('#'))
        .map((item) => ({
          value: item.value,
          contrast: Number.isFinite(item.contrast) ? item.contrast : parseFloat(item.contrast)
        }));

      valuesData.sort((a, b) => {
        const aContrast = Number.isFinite(a.contrast) ? a.contrast : 0;
        const bContrast = Number.isFinite(b.contrast) ? b.contrast : 0;
        return bContrast - aContrast;
      });

      const values = valuesData.map((item) => item.value);

      if (values.length >= count) {
        return values.slice(0, count);
      }

      if (values.length > 0) {
        const padded = [...values];
        const last = values[values.length - 1];
        while (padded.length < count) {
          padded.push(last);
        }
        return padded;
      }

      return this.generateFallbackRamp(baseColor, count);
    } catch (error) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('Leonardo theme generation error', error);
      }
      return this.generateFallbackRamp(baseColor, count);
    }
  }

  generateFallbackRamp(baseColor, count) {
    const ramp = [];
    for (let index = 0; index < count; index += 1) {
      if (!Number.isFinite(count) || count <= 1) {
        ramp.push(baseColor);
        continue;
      }
      const { min: minFactor, max: maxFactor } = this.contrastFactorRange(count);
      const normalized = Math.min(1, Math.max(0, index / Math.max(1, count - 1)));
      const factor = minFactor + normalized * (maxFactor - minFactor);
      ramp.push(this.adjustColorLegacy(baseColor, factor));
    }
    return ramp;
  }

  generateContrastRatios(count) {
    const baseRatios = [12, 9.6, 7.5, 6, 5.2, 4.5, 3.9, 3.3, 2.8, 2.4, 2.1, 1.9, 1.7, 1.5, 1.3, 1.2, 1.1];
    if (count === 2) {
      return [12, 4.5];
    }
    if (count === 3) {
      return [12, 7.5, 3.5];
    }
    if (count <= baseRatios.length) {
      return baseRatios.slice(0, count);
    }

    const ratios = baseRatios.slice();
    let next = 1.08;
    while (ratios.length < count) {
      ratios.push(next);
      next = Math.max(1.02, next - 0.03);
    }
    return ratios;
  }

  contrastFactorRange(totalAnswers) {
    if (totalAnswers <= 1) {
      return { min: 0, max: 0 };
    }
    if (totalAnswers === 2) {
      return { min: -0.85, max: 1.0 };
    }
    if (totalAnswers === 3) {
      return { min: -0.5, max: 0.9 };
    }
    return { min: -0.45, max: 0.78 };
  }

  selectLeonardoLightness(count) {
    if (count <= 2) {
      return 97;
    }
    if (count === 3) {
      return 97;
    }
    if (count <= 6) {
      return 92;
    }
    return 120;
  }

  selectLeonardoContrast(count) {
    if (count <= 2) {
      return 1.2;
    }
    if (count === 3) {
      return 1.1;
    }
    if (count <= 6) {
      return 1.1;
    }
    return 1;
  }

  normalizeHexColor(color) {
    if (typeof color !== 'string') return null;
    const trimmed = color.trim();
    if (/^#([0-9a-fA-F]{6})$/.test(trimmed)) {
      return trimmed.toUpperCase();
    }
    if (/^#([0-9a-fA-F]{3})$/.test(trimmed)) {
      const expanded = trimmed
        .slice(1)
        .split('')
        .map((char) => char + char)
        .join('');
      return `#${expanded.toUpperCase()}`;
    }
    if (/^([0-9a-fA-F]{6})$/.test(trimmed)) {
      return `#${trimmed.toUpperCase()}`;
    }
    if (/^([0-9a-fA-F]{3})$/.test(trimmed)) {
      const expanded = trimmed
        .split('')
        .map((char) => char + char)
        .join('');
      return `#${expanded.toUpperCase()}`;
    }
    return null;
  }

  renderSegmentLegend() {
    if (!this.isStackedPercentGrid) {
      this.removeCustomLegend();
      return;
    }

    const segments = Array.isArray(this.seriesNamesValue) ? this.seriesNamesValue : [];
    if (segments.length === 0) {
      this.removeCustomLegend();
      return;
    }

    this.removeCustomLegend();

    const host = this.element?.parentElement;
    if (!host) return;

    const legend = document.createElement('div');
    legend.className = 'charts-custom-legend d-flex flex-wrap justify-content-center align-items-center';
    legend.style.display = 'flex';
    legend.style.flexWrap = 'wrap';
    legend.style.justifyContent = 'center';
    legend.style.alignItems = 'center';
    legend.style.gap = '12px';
    legend.style.marginTop = '12px';
    this.segmentLegendButtons = [];

    segments.forEach((segmentName, index) => {
      const legendItem = document.createElement('button');
      legendItem.type = 'button';
      legendItem.className = 'apexcharts-legend-series';
      legendItem.setAttribute('data-segment-index', index);
      legendItem.setAttribute('aria-label', `Show segment ${segmentName}`);
      legendItem.style.background = 'rgba(255,255,255,0.9)';

      const marker = document.createElement('span');
      marker.className = 'apexcharts-legend-marker';
      const color = this.baseColorForSegment(index);
      marker.style.background = color;
      marker.style.borderColor = color;
      marker.style.display = 'inline-block';
      marker.style.width = '12px';
      marker.style.height = '12px';
      marker.style.borderRadius = '50%';
      marker.style.borderWidth = '2px';
      marker.style.borderStyle = 'solid';

      const label = document.createElement('span');
      label.className = 'apexcharts-legend-text';
      label.textContent = segmentName;
      label.style.display = 'inline-block';
      label.style.whiteSpace = 'nowrap';

      legendItem.appendChild(marker);
      legendItem.appendChild(label);
      legendItem.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleSegmentLegend(index);
      });

      legend.appendChild(legendItem);
      this.segmentLegendButtons[index] = legendItem;
    });

    if (this.element.nextSibling) {
      host.insertBefore(legend, this.element.nextSibling);
    } else {
      host.appendChild(legend);
    }
    this.legendContainer = legend;
    this.updateCustomLegendState();
  }

  pickDefaultSegmentIndex(segments) {
    if (!Array.isArray(segments) || segments.length === 0) return 0;
    const explicitAllIndex = segments.findIndex((name) => {
      if (typeof name !== 'string') return false;
      const normalized = name.trim().toLowerCase();
      return normalized === 'all' || normalized.startsWith('all ');
    });
    return explicitAllIndex >= 0 ? explicitAllIndex : 0;
  }

  setActiveSegment(segmentIndex, { force = false } = {}) {
    if (!this.chart || !this.isStackedPercentGrid) return;

    const segments = Array.isArray(this.seriesNamesValue) ? this.seriesNamesValue : [];
    if (segments.length === 0) return;

    const numericIndex = Number(segmentIndex);
    if (!Number.isFinite(numericIndex) || numericIndex < 0 || numericIndex >= segments.length) return;
    if (!force && this.activeSegmentIndex === numericIndex) return;

    const segmentSeries = this.buildStackedPercentSeriesForSegment(numericIndex);
    if (!segmentSeries) return;

    this.activeSegmentIndex = numericIndex;
    this.hiddenSegments = new Set(segments.map((_, idx) => idx).filter((idx) => idx !== numericIndex));

    // Persist segment selection for page refresh
    this.storeSegmentFilter(numericIndex);

    this.chart.updateOptions({
      series: segmentSeries.series,
      colors: segmentSeries.colors
    }, true, true);

    this.latestAnswerColors = segmentSeries.colors;
    this.latestAnswerLabels = segmentSeries.answerLabels;

    this.updateCustomLegendState();
  }

  ensureActiveSegmentIndex() {
    if (!this.isStackedPercentGrid) return;
    const segments = Array.isArray(this.seriesNamesValue) ? this.seriesNamesValue : [];
    if (segments.length === 0) return;

    // Check for stored segment preference first (restored in connect)
    let targetIndex = this.activeSegmentIndex;

    // Validate the stored/current index is within bounds for this survey's segments
    if (targetIndex === null || targetIndex < 0 || targetIndex >= segments.length) {
      // Fall back to default selection if stored value is invalid
      targetIndex = this.pickDefaultSegmentIndex(segments);
    }

    this.setActiveSegment(targetIndex, { force: true });
  }

  toggleSegmentLegend(segmentIndex) {
    if (!this.chart || !this.isStackedPercentGrid) return;

    const segmentIdx = Number(segmentIndex);
    if (!Number.isFinite(segmentIdx)) return;
    if (this.activeSegmentIndex === segmentIdx) return;

    this.setActiveSegment(segmentIdx);
  }

  syncHiddenSeriesCache() {
    if (!this.chart || !this.chart.w || !this.chart.w.globals) return;

    if (this.isStackedPercentGrid) {
      const segments = Array.isArray(this.seriesNamesValue) ? this.seriesNamesValue : [];
      this.hiddenSeries = new Set();
      this.hiddenSegments = new Set(segments.map((_, idx) => idx).filter((idx) => idx !== this.activeSegmentIndex));
      return;
    }

    const seriesConfig = this.chart.w.globals.seriesNames || [];
    const collapsedSeries = this.chart.w.globals.collapsedSeries || [];
    this.hiddenSeries = new Set();
    collapsedSeries.forEach((seriesIndex) => {
      const name = seriesConfig[seriesIndex];
      if (name) {
        this.hiddenSeries.add(name);
      }
    });
  }

  updateCustomLegendState() {
    if (!this.isStackedPercentGrid || !Array.isArray(this.segmentLegendButtons)) return;
    this.syncHiddenSeriesCache();

    this.segmentLegendButtons.forEach((button, index) => {
      if (!button) return;
      const numericIndex = Number(index);
      const isSelected = this.activeSegmentIndex === numericIndex;
      const isHidden = !isSelected;
      button.classList.toggle('apexcharts-inactive-legend', isHidden);
      button.classList.toggle('charts-custom-legend-active', isSelected);
      button.style.opacity = isSelected ? '1' : '0.45';
      button.style.background = isSelected ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.9)';
      button.style.borderColor = isSelected ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)';
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      const marker = button.querySelector('.apexcharts-legend-marker');
      if (marker) {
        marker.style.opacity = isSelected ? '1' : '0.35';
        marker.style.borderColor = marker.style.background;
      }
    });
  }

  removeCustomLegend() {
    if (this.legendContainer && this.legendContainer.parentNode) {
      this.legendContainer.parentNode.removeChild(this.legendContainer);
    }
    this.legendContainer = null;
    this.segmentLegendButtons = [];
  }

  updateStackedPercentGridData() {
    if (!this.chart || !this.isStackedPercentGrid) return;

    const categories = this.prepareLabels(this.datafieldLabelsValue || []);

    this.chart.updateOptions({
      xaxis: {
        categories,
        labels: {
          rotate: 0,
          style: {
            fontSize: this.calculateXAxisLabelFontSize(categories.length),
            fontWeight: 'bold'
          },
          maxHeight: 200
        }
      },
      chart: {
        height: this.calculateStackedChartHeight()
      }
    }, false, false);

    this.renderSegmentLegend();
    this.ensureActiveSegmentIndex();
  }

  renderStackedTooltip(series, seriesIndex, dataPointIndex, w) {
    const configSeries = w?.config?.series || [];
    const globals = w?.globals || {};
    const seriesItem = configSeries[seriesIndex];
    if (!seriesItem) return '';

    const meta = seriesItem.meta || {};

    // For stacked percentage grids, the value IS the percentage (not a count)
    const rawSeriesValue = globals.series?.[seriesIndex]?.[dataPointIndex];
    const seriesValue = Number.isFinite(rawSeriesValue) ? Number(rawSeriesValue) : 0;

    const rawFieldLabel = Array.isArray(this.datafieldLabelsValue?.[dataPointIndex])
      ? this.datafieldLabelsValue[dataPointIndex].join(' ')
      : this.datafieldLabelsValue?.[dataPointIndex];
    const dataFieldLabel = this.escapeHtml(rawFieldLabel || '');

    const segmentName = this.escapeHtml(meta.segmentName || this.seriesNamesValue?.[meta.segmentIndex] || seriesItem.name || '');
    const answerLabel = this.escapeHtml(
      seriesItem.name ||
      meta.answerLabel ||
      this.answerLabelsValue?.[meta.answerIndex] ||
      `Option ${(meta.answerIndex ?? -1) + 1}`
    );
    const color = seriesItem.color || this.baseColorForSegment(meta.segmentIndex || 0);

    // Display the actual percentage value (seriesValue is already a percentage)
    const percentDisplay = Number.isFinite(seriesValue) ? `${Math.round(seriesValue)}%` : '0%';

    return `<div class="apexcharts-tooltip-custom" style="padding:10px 12px;background:#333;color:#fff;border-radius:6px;font-size:12px;min-width:190px;">
      <div style="font-weight:600;margin-bottom:6px;">${dataFieldLabel}</div>
      <div style="display:flex;align-items:center;margin-bottom:4px;">
        <span style="display:inline-block;width:10px;height:10px;background:${color};border-radius:50%;margin-right:6px;border:2px solid #fff;"></span>
        <span style="font-weight:600;">${segmentName}</span>
      </div>
      <div style="display:flex;justify-content:space-between;gap:12px;font-size:13px;">
        <span>${answerLabel}</span>
        <strong>${percentDisplay}</strong>
      </div>
    </div>`;
  }

  escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Adds series data to the chart and sets x-axis categories.
   * @param {Array<Array<number>>} series - Data for each series
   * @param {Array<string>} labels - X-axis labels
   */
  buildChart(series, labels) {
    if (!this.chart || !series || !labels) return;

    // Prepare ApexCharts series format
    let formattedSeries = [];
    for (let i = 0; i < series.length; i++) {
      if (series[i] && this.seriesNamesValue[i]) {
        formattedSeries.push({
          name: this.seriesNamesValue[i],
          data: series[i]
        });
      }
    }

    // Convert long string labels to array-of-lines for multiline display
    // Note: X-axis labels (categories) are always shown - only 0% data labels are hidden
    const preparedLabels = this.prepareLabels(labels);
    // Determine optimal label rotation based on prepared labels
    const shouldRotateLabels = this.shouldRotateLabels(preparedLabels);

    // Track which labels are actively used for resize handling
    this.activeXAxisLabels = labels;

    // Update chart with new data and optimized label settings
    this.chart.updateOptions({
      xaxis: {
        categories: preparedLabels,
        labels: {
          rotate: 0, // No rotation - keep labels vertical/horizontal
          style: {
            fontSize: this.calculateXAxisLabelFontSize(labels.length),
            fontWeight: 'bold'
          }
        }
      },
      dataLabels: {
        style: {
          fontSize: this.calculateDataLabelFontSize(labels.length),
          fontWeight: 'bold',
          colors: ['#000000']
        }
      },
      series: formattedSeries
    });
  }

  /**
   * Finds indices of columns that have at least one non-zero value across all series.
   * @param {Array} formattedSeries - Array of series objects with data arrays
   * @returns {Array<number>} Indices of columns with non-zero values
   */
  findNonZeroColumnIndices(formattedSeries) {
    if (!formattedSeries || formattedSeries.length === 0) return [];
    const columnCount = formattedSeries[0]?.data?.length || 0;
    const nonZeroIndices = [];

    for (let colIndex = 0; colIndex < columnCount; colIndex++) {
      const hasNonZero = formattedSeries.some(series => {
        const value = series.data[colIndex];
        return typeof value === 'number' && value !== 0;
      });
      if (hasNonZero) {
        nonZeroIndices.push(colIndex);
      }
    }

    return nonZeroIndices;
  }

  // No-op: ApexCharts multiline now handled via array categories
  processMultiLineLabels() { }

  /**
   * Determines if labels should be rotated based on their length and count.
   * Always returns false - we use vertically stacked multiline labels instead.
   * @param {Array} labels - X-axis labels (may contain arrays for multiline)
   * @returns {boolean} Whether labels should be rotated
   */
  shouldRotateLabels(labels) {
    if (!labels || labels.length === 0) return false;

    // Always use vertical (no rotation) with multiline labels
    // This ensures consistent stacked label display
    return false;
  }

  /**
   * Efficiently updates chart data without full reload.
   * This is the performance-optimized method for real-time updates.
   * @param {Array<Array<number>>} newSeriesData - New series data
   * @param {Array<string>} newLabels - New x-axis labels  
   */
  updateChartData(newSeriesData, newLabels) {
    if (!this.chart) return;

    if (this.isStackedPercentGrid) {
      this.updateStackedPercentGridData();
      return;
    }

    if (!newSeriesData) return;

    // Format for ApexCharts
    const formattedSeries = [];
    for (let i = 0; i < newSeriesData.length; i++) {
      if (newSeriesData[i] && this.seriesNamesValue[i]) {
        formattedSeries.push({
          name: this.seriesNamesValue[i],
          data: newSeriesData[i]
        });
      }
    }

    // Use ApexCharts' efficient updateSeries method
    this.chart.updateSeries(formattedSeries);

    // Update categories with optimized label settings if labels changed
    if (newLabels) {
      const preparedLabels = this.prepareLabels(newLabels);
      const shouldRotateLabels = this.shouldRotateLabels(preparedLabels);

      this.chart.updateOptions({
        xaxis: {
          categories: preparedLabels,
          labels: {
            rotate: 0, // No rotation - keep labels vertical/horizontal
            maxHeight: 200,
            style: {
              fontSize: this.calculateXAxisLabelFontSize(newLabels.length),
              fontWeight: 'bold'
            }
          }
        },
        dataLabels: {
          style: {
            fontSize: this.calculateDataLabelFontSize(newLabels.length),
            fontWeight: 'bold',
            colors: ['#000000']
          }
        }
      }, false, false); // Don't redraw, don't animate for performance

    }
  }

  /**
   * Prepare labels for ApexCharts multiline rendering by converting long strings
   * into arrays of words/phrases per the official ApexCharts documentation.
   * Each array element becomes a line in the chart.
   *
   * Uses canvas text measurement to intelligently wrap labels to fit available width.
   */
  prepareLabels(labels) {
    // Calculate available width per label
    const columnCount = labels.length || 1;
    const chartWidth = this.getChartWidth();
    const availableWidthPerLabel = Math.floor(chartWidth / columnCount) - 20; // 20px padding per label

    return labels.map(label => {
      if (Array.isArray(label)) return label; // already multiline
      if (typeof label !== 'string') return label;

      // If contains newlines, split on them
      if (label.includes('\n')) {
        return label.split('\n');
      }

      // Use intelligent word wrapping based on measured text width
      return this.wrapLabelToWidth(label, availableWidthPerLabel);
    });
  }

  /**
   * Get the current chart container width, accounting for CSS transforms
   * @returns {number} Width in pixels
   */
  getChartWidth() {
    if (!this.element) return 800; // default fallback

    // Get the actual rendered width
    const width = this.element.offsetWidth || this.element.clientWidth;

    // If width is 0 (not yet rendered), try parent column
    if (width === 0) {
      const parentColumn = this.element.closest('.col-md-9');
      if (parentColumn) {
        return parentColumn.offsetWidth || 1032; // Bootstrap col-md-9 default
      }
    }

    return width || 800; // fallback
  }

  /**
   * Wrap a label string to fit within a specified width using canvas text measurement.
   * Breaks at word boundaries first, then breaks long words mid-character if needed.
   * @param {string} label - The label text to wrap
   * @param {number} maxWidth - Maximum width in pixels for each line
   * @returns {string|Array<string>} Original string if it fits, or array of wrapped lines
   */
  wrapLabelToWidth(label, maxWidth) {
    // Get font size for measurement
    const fontSize = this.calculateXAxisLabelFontSize(this.answerLabelsValue?.length || this.datafieldLabelsValue?.length || 0);
    const fontWeight = 'bold';
    const font = `${fontWeight} ${fontSize} Verdana, Lucida, Arial, Helvetica, sans-serif`;

    // Measure text using canvas
    const measureText = (text) => {
      if (!this.measureCanvas) {
        this.measureCanvas = document.createElement('canvas');
        this.measureContext = this.measureCanvas.getContext('2d');
      }
      this.measureContext.font = font;
      return this.measureContext.measureText(text).width;
    };

    // Break a single word into chunks that fit maxWidth
    const breakWord = (word) => {
      const chunks = [];
      let remaining = word;
      while (remaining.length > 0) {
        // Find how many characters fit
        let end = remaining.length;
        while (end > 1 && measureText(remaining.slice(0, end)) > maxWidth) {
          end--;
        }
        chunks.push(remaining.slice(0, end));
        remaining = remaining.slice(end);
      }
      return chunks;
    };

    // Check if label fits on one line
    const fullWidth = measureText(label);
    if (fullWidth <= maxWidth) {
      return label; // fits on one line
    }

    // Split into words and build lines that fit maxWidth
    const words = label.split(' ');
    const lines = [];
    let currentLine = '';

    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      // Check if this single word exceeds maxWidth - if so, break it
      if (measureText(word) > maxWidth) {
        // Push current line first if it has content
        if (currentLine) {
          lines.push(currentLine);
          currentLine = '';
        }
        // Break the long word into chunks
        const chunks = breakWord(word);
        // Add all chunks except the last as complete lines
        for (let j = 0; j < chunks.length - 1; j++) {
          lines.push(chunks[j]);
        }
        // The last chunk becomes the current line (may combine with next word)
        currentLine = chunks[chunks.length - 1];
        continue;
      }

      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = measureText(testLine);

      if (testWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        // Current line is full, start new line
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    }

    // Add the last line
    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [label];
  }

  /**
   * Stores the current series visibility state before data updates.
   */
  storeSeriesVisibility() {
    if (!this.chart || !this.chart.w || !this.chart.w.globals) return;
    this.syncHiddenSeriesCache();
  }

  /**
   * Restores the series visibility state after data updates.
   */
  restoreSeriesVisibility() {
    if (!this.chart) {
      return;
    }

    const hasHiddenSeries = this.hiddenSeries && this.hiddenSeries.size > 0;
    const hasHiddenSegments = this.hiddenSegments && this.hiddenSegments.size > 0;

    if (!hasHiddenSeries && !hasHiddenSegments) {
      this.updateCustomLegendState();
      return;
    }

    const chartSeriesNames = (this.chart.w && this.chart.w.config && Array.isArray(this.chart.w.config.series))
      ? this.chart.w.config.series.map((entry) => entry.name)
      : [];

    const hiddenSeriesSnapshot = new Set(this.hiddenSeries || []);

    if (this.isStackedPercentGrid) {
      setTimeout(() => {
        this.ensureActiveSegmentIndex();
        this.syncHiddenSeriesCache();
        this.updateCustomLegendState();
      }, 100);
      return;
    }

    setTimeout(() => {
      this.seriesNamesValue.forEach((seriesName) => {
        if (hiddenSeriesSnapshot.has(seriesName)) {
          this.chart.hideSeries(seriesName);
        }
      });
      this.syncHiddenSeriesCache();
      this.updateCustomLegendState();
    }, 100);
  }

  /**
   * Removes all series from the chart.
   */
  clearSeries() {
    if (this.chart && typeof this.chart.updateSeries === 'function') {
      this.chart.updateSeries([]);
    }
  }

  /**
   * Randomly updates a point in the given series (for demo/testing).
   * @param {number} index - Series index
   */
  randomUpdate(index) {
    if (!this.chart || !this.chart.w || !this.chart.w.config.series[index]) return;

    const series = this.chart.w.config.series[index];
    const x = Math.floor(Math.random() * series.data.length);
    const currentValue = series.data[x];
    const newValue = Math.max(0, currentValue - Math.random() * 2 + Math.random() * 2);

    // Update single data point efficiently
    const newSeries = [...this.chart.w.config.series];
    newSeries[index] = {
      ...series,
      data: series.data.map((val, idx) => idx === x ? newValue : val)
    };

    this.chart.updateSeries(newSeries, false); // Don't animate for performance
  }

  /**
   * Returns indices of non-exclusive data fields for Acutext rendering.
   * Exclusive fields (like "None of these") should appear in tables but not word clouds.
   */
  getAcutextVisibleIndices() {
    const exclusiveFlags = this.datafieldExclusiveValue || [];
    // Return indices of non-exclusive fields (exclude options like "None of these")
    return this.datafieldLabelsValue
      .map((_, i) => i)
      .filter(i => !exclusiveFlags[i]);
  }

  /**
   * Renders Acutext word cloud by creating a span for each word.
   * Each word is given a unique id for later animation.
   * Exclusive fields (like "None of these") are filtered out of the word cloud.
   */
  prepare_acutext() {
    this.chart.innerHTML = "";
    const visibleIndices = this.getAcutextVisibleIndices();
    for (let displayIndex = 0; displayIndex < visibleIndices.length; displayIndex++) {
      const originalIndex = visibleIndices[displayIndex];
      const word = this.datafieldLabelsValue[originalIndex];
      const output_AT = this.chart.innerHTML;
      // Use displayIndex for DOM id to maintain sequential numbering for animation
      this.chart.innerHTML = (output_AT + `<span class='acutext_word' id='word_${displayIndex}' style='font-size:20px; color: black'> ${word}&nbsp</span>`);
    }
  }

  /**
   * Animates Acutext word cloud font sizes based on mean values.
   * Larger means = larger font size with reasonable scaling.
   * Exclusive fields are filtered out to match prepare_acutext() rendering.
   */
  adjust_acutext() {
    if (!this.seriesMeansValue || !this.seriesMeansValue[0]) return;

    const means = this.seriesMeansValue[0];
    const visibleIndices = this.getAcutextVisibleIndices();

    // Filter means to only include visible (non-exclusive) fields
    const visibleMeans = visibleIndices.map(i => means[i] || 0);

    if (visibleMeans.length === 0) return;

    const maxMean = Math.max(...visibleMeans);
    const minMean = Math.min(...visibleMeans);
    const meanRange = maxMean - minMean;

    // Base font sizes: 10px to 40px range (increased spread for more dramatic differences)
    const minFontSize = 10;
    const maxFontSize = 40;
    const fontRange = maxFontSize - minFontSize;

    for (let displayIndex = 0; displayIndex < visibleIndices.length; displayIndex++) {
      const mean = visibleMeans[displayIndex];
      let size;

      if (meanRange === 0) {
        // All means are the same, use medium size
        size = (minFontSize + maxFontSize) / 2;
      } else {
        // Scale proportionally within the font size range
        const normalizedValue = (mean - minMean) / meanRange;
        size = minFontSize + (normalizedValue * fontRange);
      }

      // Ensure reasonable bounds and add px unit
      size = Math.max(minFontSize, Math.min(maxFontSize, size));
      $(`#word_${displayIndex}`).animate({ fontSize: size + "px" }, 500);
    }
  }

  /**
   * Adds an ApexCharts-style toolbar with dropdown menu to the word cloud view.
   * Replicates the exact DOM structure and CSS classes used by ApexCharts.
   */
  addWordCloudDownloadButton() {
    // Toolbar wrapper — matches ApexCharts toolbar positioning
    const toolbar = document.createElement('div');
    toolbar.className = 'wordcloud-toolbar';
    toolbar.style.cssText = 'position: absolute; top: 0; right: 3px; z-index: 11; text-align: right; padding: 0 6px 2px;';

    // Hamburger menu icon — matches ApexCharts .apexcharts-menu-icon
    const menuIcon = document.createElement('div');
    menuIcon.title = 'Menu';
    menuIcon.style.cssText = 'cursor: pointer; width: 20px; height: 20px; line-height: 24px; color: #6e8192; display: inline-block;';
    menuIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#6e8192"><path fill="none" d="M0 0h24v24H0V0z"/><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/></svg>';

    // Dropdown menu — inline styles matching ApexCharts .apexcharts-menu
    const menu = document.createElement('div');
    menu.style.cssText = 'background: #fff; position: absolute; top: 100%; right: 0; border: 1px solid #ddd; border-radius: 3px; padding: 3px; opacity: 0; pointer-events: none; min-width: 110px; transition: 0.15s ease all;';

    const menuItems = [
      { label: 'Download SVG', handler: () => this.exportWordCloudSVG() },
      { label: 'Download PNG', handler: () => this.exportWordCloudPNG() },
    ];

    menuItems.forEach(({ label, handler }) => {
      const item = document.createElement('div');
      item.textContent = label;
      item.title = label;
      item.style.cssText = 'padding: 6px 7px; font-size: 12px; cursor: pointer;';
      item.addEventListener('mouseenter', () => { item.style.background = '#eee'; });
      item.addEventListener('mouseleave', () => { item.style.background = ''; });
      item.addEventListener('click', () => {
        menu.style.opacity = '0';
        menu.style.pointerEvents = 'none';
        handler();
      });
      menu.appendChild(item);
    });

    // Toggle menu on icon click
    menuIcon.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = menu.style.opacity === '1';
      menu.style.opacity = isOpen ? '0' : '1';
      menu.style.pointerEvents = isOpen ? 'none' : 'all';
    });

    // Close menu on outside click — stored so it can be removed in disconnect()
    this.wordCloudClickHandler = (e) => {
      if (!toolbar.contains(e.target)) {
        menu.style.opacity = '0';
        menu.style.pointerEvents = 'none';
      }
    };
    document.addEventListener('click', this.wordCloudClickHandler);

    toolbar.appendChild(menuIcon);
    toolbar.appendChild(menu);

    this.element.style.position = 'relative';
    this.element.appendChild(toolbar);
  }

  /**
   * Prepares the word cloud element for export by hiding the toolbar
   * and adding a temporary title. Returns a cleanup function.
   */
  prepareWordCloudForExport() {
    const toolbar = this.element.querySelector('.wordcloud-toolbar');
    if (toolbar) toolbar.style.display = 'none';

    return () => {
      if (toolbar) toolbar.style.display = '';
    };
  }

  /**
   * Exports the word cloud as a PNG using html2canvas.
   * Hides toolbar and adds title to match ApexCharts export format.
   */
  async exportWordCloudPNG() {
    const filename = this.getExportFilename();
    const cleanup = this.prepareWordCloudForExport();
    try {
      const canvas = await html2canvas(this.element, { backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${filename}.png`;
      link.click();
    } catch (err) {
      console.error('Word cloud PNG export failed:', err);
    } finally {
      cleanup();
    }
  }

  /**
   * Exports the word cloud as an SVG.
   * Builds an SVG from the word cloud span elements, matching their computed styles.
   */
  exportWordCloudSVG() {
    const filename = this.getExportFilename();
    const words = this.element.querySelectorAll('.acutext_word');
    if (!words.length) return;

    // Hide toolbar before measuring so positions match PNG export
    const cleanup = this.prepareWordCloudForExport();

    const rect = this.element.getBoundingClientRect();
    const svgWidth = rect.width;
    const svgHeight = rect.height;

    let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" style="background: white;">`;

    // Add each word, positioned relative to the container
    words.forEach((word) => {
      const wordRect = word.getBoundingClientRect();
      const computed = window.getComputedStyle(word);
      const x = wordRect.left - rect.left;
      const y = wordRect.top - rect.top + parseFloat(computed.fontSize) * 0.8;
      const fontSize = computed.fontSize;
      const color = computed.color;

      svgContent += `<text x="${x}" y="${y}" font-family="Helvetica, Arial, sans-serif" font-size="${fontSize}" fill="${color}">${this.escapeXml(word.textContent.trim())}</text>`;
    });

    svgContent += '</svg>';

    cleanup();

    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.svg`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Escapes XML special characters for safe SVG text content.
   */
  escapeXml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /**
   * Initializes a horizontal bar chart for Acutext data.
   * Alternative to word cloud visualization.
   */
  initAcutextBarChart() {
    const options = {
      chart: {
        type: 'bar',
        height: 600, // Fixed height to ensure bars are tall enough
        background: 'transparent',
        animations: {
          enabled: false,
          easing: 'easeinout',
          speed: 800
        },
        toolbar: this.getToolbarConfig()
      },
      title: {
        text: '', // Title hidden on display, added dynamically for exports
        align: 'center',
        style: {
          fontSize: '14px',
          fontWeight: 'bold'
        }
      },
      series: [],
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'center'
          },
          barHeight: '85%', // Increased from 70% to 85% for thicker bars
          distributed: false
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '14px', // Increased from 12px to 14px
          fontWeight: 'bold',
          colors: ['#ffffff']
        },
        formatter: function (value) {
          return value.toFixed(1);
        },
        offsetX: 0,
        offsetY: 0
      },
      xaxis: {
        categories: [],
        title: {
          text: 'Frequency/Importance Score',
          style: {
            fontSize: '10px',
            fontWeight: 'bold'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Words/Phrases',
          offsetX: 4,
          style: {
            fontSize: '10px',
            fontWeight: 'bold'
          }
        }
      },
      colors: this.colorsValue || ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '12px',
        fontWeight: '600',
        offsetY: 5,
        markers: {
          shape: 'circle',
          width: 10,
          height: 10,
          strokeWidth: 0
        },
        itemMargin: {
          horizontal: 4,
          vertical: 4
        },
        onItemClick: {
          toggleDataSeries: true
        },
        onItemHover: {
          highlightDataSeries: true
        }
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: {
          formatter: function (value) {
            return 'Score: ' + value.toFixed(1);
          }
        }
      }
    };

    this.chart = new ApexCharts(this.element, options);
    this.chart.render();
  }

  /**
   * Builds the Acutext bar chart with grouped segment data from seriesMeans.
   * Exclusive fields (like "None of these") are filtered out of the bar chart.
   */
  buildAcutextBarChart() {
    if (!this.chart || !this.seriesMeansValue || this.seriesMeansValue.length === 0) return;

    // Get visible indices (filtering out exclusive fields like "None of these")
    const visibleIndices = this.getAcutextVisibleIndices();

    // Get the actual word/phrase labels, filtered to exclude exclusive fields
    const wordLabels = visibleIndices.map(i => this.datafieldLabelsValue[i]);

    if (wordLabels.length === 0) {
      console.warn('No word labels available for Acutext bar chart');
      return;
    }

    // Prepare series data for each segment
    const formattedSeries = [];
    for (let segmentIndex = 0; segmentIndex < this.seriesMeansValue.length; segmentIndex++) {
      const segmentData = this.seriesMeansValue[segmentIndex];
      if (segmentData && segmentData.length > 0) {
        // Use segment name from seriesNames, with fallback
        const segmentName = this.seriesNamesValue && this.seriesNamesValue[segmentIndex]
          ? this.seriesNamesValue[segmentIndex]
          : `Segment ${segmentIndex + 1}`;

        // Filter data points to only include visible (non-exclusive) fields
        const dataPoints = visibleIndices.map(i => segmentData[i] || 0);

        formattedSeries.push({
          name: segmentName,
          data: dataPoints
        });
      }
    }

    // If no valid series data, create a placeholder
    if (formattedSeries.length === 0) {
      console.warn('No valid series data for Acutext bar chart');
      return;
    }

    // Helper function to truncate long labels for better display
    const truncateLabel = (label, maxLength = 80) => { // Increased from 50 to 80 for better visibility
      if (typeof label !== 'string') return String(label);
      if (label.length <= maxLength) return label;

      // Try to break at word boundaries
      const truncated = label.substring(0, maxLength);
      const lastSpace = truncated.lastIndexOf(' ');
      if (lastSpace > maxLength * 0.7) { // Back to original threshold
        return truncated.substring(0, lastSpace) + '...';
      }
      return truncated + '...';
    };

    // Sort labels by the highest value across all segments for better readability
    const labelValuePairs = wordLabels.map((label, index) => {
      const maxValue = Math.max(...formattedSeries.map(series => series.data[index] || 0));
      const displayLabel = truncateLabel(label);
      return { label: displayLabel, originalLabel: label, index, maxValue };
    }).sort((a, b) => b.maxValue - a.maxValue);

    // Reorder data based on sorted labels
    const sortedLabels = labelValuePairs.map(item => item.label);
    const sortedSeries = formattedSeries.map(series => ({
      ...series,
      data: labelValuePairs.map(item => series.data[item.index] || 0)
    }));

    // Calculate dynamic height based on number of data points
    // Use generous spacing to ensure text fits: 50px per item minimum, no maximum limit
    const pixelsPerItem = Math.max(50, Math.min(80, 3000 / sortedLabels.length)); // Scale down per item for very large datasets
    const calculatedHeight = Math.max(400, sortedLabels.length * pixelsPerItem);

    console.log(`Acutext chart sizing: ${sortedLabels.length} items × ${pixelsPerItem}px = ${calculatedHeight}px total height`);

    // Make container scrollable for very tall charts
    if (calculatedHeight > 1200) {
      this.element.style.maxHeight = '1200px';
      this.element.style.overflowY = 'auto';
      this.element.style.border = '1px solid #ddd';
      this.element.style.borderRadius = '4px';
    } else {
      this.element.style.maxHeight = '';
      this.element.style.overflowY = '';
      this.element.style.border = '';
      this.element.style.borderRadius = '';
    }

    // For horizontal bar charts, categories go on xaxis (not yaxis!)
    // This defines the labels that appear on the left side of the horizontal bars
    const updateConfig = {
      chart: {
        height: calculatedHeight
      },
      xaxis: {
        categories: sortedLabels,
        labels: {
          style: {
            fontSize: '10px',
            fontWeight: 'normal',
            cssClass: 'apexcharts-yaxis-label-truncate' // Add CSS class for ellipsis styling
          },
          maxWidth: 300, // Increased from 250 to 300 for better label visibility
          trim: false, // Changed from true - let CSS handle truncation with ellipsis
          offsetX: -10 // Add offset to prevent overlap with chart bars
        }
      },
      yaxis: {
        min: 0,
        max: 105, // Extended to 105% to ensure 100% values aren't cut off
        labels: {
          formatter: function (value) {
            const numValue = Number(value);
            return isNaN(numValue) ? value : numValue.toFixed(0) + '%';
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '11px',
          fontWeight: 'bold',
          colors: ['#000000'] // Changed to black as requested
        },
        formatter: function (value) {
          const numValue = Number(value);
          if (isNaN(numValue)) return '';
          return numValue.toFixed(1); // No rounding - show 1 decimal place
        },
        offsetX: 5, // Small space from end of bar
        offsetY: 0,
        textAnchor: 'start' // Align text to start from the position (end of bar)
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            position: 'top' // Position at end of horizontal bars
          },
          barHeight: '80%',
          distributed: false,
          borderRadius: 3,
          columnWidth: '90%'
        }
      },
      tooltip: {
        enabled: true,
        theme: 'dark',
        y: {
          formatter: function (value) {
            return 'Score: ' + value.toFixed(1);
          }
        },
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          // Show full original text in tooltip
          const originalLabel = labelValuePairs[dataPointIndex]?.originalLabel || sortedLabels[dataPointIndex];
          const value = series[seriesIndex][dataPointIndex];
          const seriesName = w.globals.seriesNames[seriesIndex];

          return `<div class="apexcharts-tooltip-custom" style="padding: 8px; background: #333; color: white; border-radius: 4px; font-size: 12px;">
            <strong>${seriesName}</strong><br/>
            <span style="font-size: 11px;">${originalLabel}</span><br/>
            <strong>Score: ${value.toFixed(1)}</strong>
          </div>`;
        }
      },
      series: sortedSeries,
      colors: this.colorsValue || ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0']
    };

    // Use updateOptions with redraw to ensure proper update
    this.chart.updateOptions(updateConfig, false, true, true);
  }

  // TODO: Cleanup or remove unused methods
  // These methods were commented out in the original code and may not be needed.
  // $('.area').mousedown(function() {
  //   c = $(this).attr("area_index");
  //   $(".checkBox[data-group="+c+"]").click();

  //   acutext_refresh();
  // })

  // refresh_acutext() {
  //   $('.area').each(function() {
  //     c = $(this).attr("area_index");
  //     selected = $(".checkBox[data-group="+c+"]").attr('value');
  //     if(selected == 'true'){
  //       $(this).addClass("area_HL");
  //     } else {
  //       $(this).removeClass("area_HL");
  //     }
  //   })
  // }

}
